
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface EmotionDisplayProps {
  emotions: Record<string, number>;
  speechRate: number;
}

const EmotionDisplay = ({ emotions, speechRate }: EmotionDisplayProps) => {
  // Format data for the chart
  const emotionData = Object.entries(emotions).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value: Math.round(value * 100),
  }));

  // Determine the primary emotion (highest value)
  const primaryEmotion = emotionData.reduce(
    (max, emotion) => (emotion.value > max.value ? emotion : max),
    { name: "", value: 0 }
  );

  // Speech rate feedback
  const getSpeechRateFeedback = (rate: number) => {
    if (rate < 100) return "Your speech rate is slow. Consider speaking slightly faster for better engagement.";
    if (rate > 160) return "Your speech rate is fast. Consider slowing down for better clarity.";
    return "Your speech rate is at an optimal pace for good comprehension.";
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Detected Emotions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={emotionData} layout="vertical">
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="name" type="category" width={80} />
                <Tooltip formatter={(value) => [`${value}%`, "Confidence"]} />
                <Bar 
                  dataKey="value" 
                  fill="#8884d8" 
                  radius={[0, 4, 4, 0]}
                  background={{ fill: "#eee" }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Primary Emotion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center p-4">
              <div className="text-center">
                <span className="text-4xl font-bold text-indigo-600">{primaryEmotion.name}</span>
                <p className="text-sm text-gray-500 mt-2">Confidence: {primaryEmotion.value}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Speech Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center p-4 space-y-2">
              <span className="text-2xl font-bold">{speechRate} words/min</span>
              <p className="text-sm text-gray-600 text-center">
                {getSpeechRateFeedback(speechRate)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmotionDisplay;
